<template>
    <div class="cronograma-container">
        <div v-if="loading" class="text-center p-4">
            <b-spinner label="Carregando..."></b-spinner>
        </div>

        <div v-else>
            <boletim-medicao-cronograma-table
                v-if="viewMode === 'tabela'"
                :lista-itens="listaItens"
                :dados-total="dadosTotal"
                :indice-fixo-bm="indiceFixoBm"
                :exibir-realizado="exibirRealizado"
                :exibir-todos-bms="exibirTodosBms"
                :contrato-id="contratoId"
                :boletim-medicao-id="boletimMedicaoId"
                :lista-boletins="listaBoletinsDoAditivo"
            />

            <div v-else-if="viewMode === 'pdf-viewer'" class="pdf-viewer-container">
                <div v-if="carregandoPdfParaAssinatura" class="loading-overlay">
                    <div class="spinner-container">
                        <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
                        <p class="mt-3 text-primary font-weight-bold">Gerando PDF...</p>
                    </div>
                </div>

                <div
                    v-if="pdfUrl"
                    class="pdf-file-name"
                    :title="nomeArquivoPreviewCronograma"
                >
                    {{ nomeArquivoPreviewCronograma }}
                </div>

                <iframe
                    v-if="pdfUrl"
                    :src="srcVisualizacaoPdfCronograma"
                    width="100%"
                    height="600px"
                    frameborder="0"
                ></iframe>
            </div>

            <relatorio-para-exportacao
                v-else-if="viewMode === 'impressao'"
                :contrato="dadosContratoRelatorio"
                :boletim-medicao="dadosBoletimRelatorio"
                :itens="listaItens"
                :total="dadosTotal"
                :indice-fixo-bm="indiceFixoBm"
            />

            <upload-arquivo-assinado
                v-else-if="viewMode === 'upload'"
                :contrato-id="contratoId"
                :disable-enviar="loadingUpload"
                :nome-arquivo-existente="nomeAnexoCronogramaAssinado"
                @file-selected="arquivoSelecionado = $event"
                @enviar-repositorio="enviarArquivoAssinado"
            />

            <cronograma-acoes
                :view-mode="viewMode"
                :disable-enviar="loadingUpload"
                :disable-geracao-pdf="carregandoPdfParaAssinatura"
                :disable-botao-gerar-pdf="!podeGerarPdfParaAssinatura"
                :texto-botao-gerar-pdf="textoBotaoGerarPdfAssinatura"
                :fluxo-interno="false"
                @voltar-lista-boletins="voltarParaListaBoletins"
                @gerar-pdf-assinatura="gerarPdfParaAssinatura"
                @gerar-pdf="mudarEtapa('impressao')"
                @cancelar-impressao="mudarEtapa('tabela')"
                @baixar-pdf="gerarPdfBrowser"
                @proxima-etapa="mudarEtapa('upload')"
                @voltar-para-impressao="onVoltarUpload"
                @voltar-para-tabela="mudarEtapa('tabela')"
            />
        </div>
    </div>
</template>

<script>
import BoletimMedicaoServicos from '../../BoletimMedicaoServicos'
import { statusBoletimMedicaoEnum } from '../../BoletimMedicao'
import { tiposAnexoEnum } from '../../tiposAnexo'
import CronogramaAcoes from './cronograma-planejamento/CronogramaAcoes.vue'
import BoletimMedicaoCronogramaTable from './cronograma-planejamento/BoletimMedicaoCronogramaTable.vue'
import RelatorioParaExportacao from './cronograma-planejamento/RelatorioParaExportacao.vue'
import UploadArquivoAssinado from './cronograma-planejamento/UploadArquivoAssinado.vue'

export default {
    name: 'BoletimMedicaoCronograma',
    components: {
        CronogramaAcoes,
        BoletimMedicaoCronogramaTable,
        RelatorioParaExportacao,
        UploadArquivoAssinado
    },
    props: {
        itensExternos: { type: Array, default: () => [] },
        totalExterno: { type: Object, default: () => null },
        indiceFixoBm: { type: [String, Number], default: 0 },
        contratoId: { type: [Number, String], default: 0 },
        boletimMedicaoId: { type: [Number, String], default: 0 },
        boletimMedicao: { type: Object, default: () => null },
        contrato: { type: Object, default: () => ({}) },
        todosBoletinsMedicoes: { type: Array, default: () => [] },
        exibirRealizado: { type: Boolean, default: true },
        exibirTodosBms: { type: Boolean, default: false },
        logoFornecedorUrl: { type: String, default: null } // Add this new prop
    },
    data() {
        return {
            loading: false,
            loadingUpload: false,
            carregandoPdfParaAssinatura: false,
            viewMode: 'tabela',
            arquivoSelecionado: null,
            itensInternos: [],
            totalInterno: null,
            dadosContrato: {},
            dadosBoletim: {},
            pdfUrl: null,
            listaBoletinsDoAditivo: []
        }
    },
    computed: {
        temDadosExternos() {
            return this.itensExternos && this.itensExternos.length > 0
        },
        listaItens() {
            const itens = this.temDadosExternos ? this.itensExternos : this.itensInternos
            return itens
        },
        dadosTotal() {
            const total = this.temDadosExternos ? this.totalExterno : this.totalInterno
            return total
        },
        dadosContratoRelatorio() {
            const contratoDoBoletim = this.boletimMedicao && this.boletimMedicao.contrato ? this.boletimMedicao.contrato : null;
            if (!contratoDoBoletim) return this.dadosContrato;
            return { ...this.dadosContrato, ...contratoDoBoletim };
        },
        dadosBoletimRelatorio() {
            const temPeriodoNoBoletim = this.boletimMedicao &&
                (this.boletimMedicao.dataInicio || this.boletimMedicao.dataFim);
            return temPeriodoNoBoletim ? this.boletimMedicao : this.dadosBoletim;
        },
        podeGerenciarAssinaturaAtual() {
            return (this.$store.getters.isFornecedor
                && this.boletimMedicao?.status === statusBoletimMedicaoEnum.AprovadoParaColetaAssinaturaFornecedor)
                || (this.$store.getters.isFornecedor
                    && this.boletimMedicao?.status === statusBoletimMedicaoEnum.DevolvidoParaReassinarFornecedor
                    && this.boletimMedicao?.cronogramaEditavel)
                || (this.$temAcessoView('FISCAL-TERCEIRO')
                    && this.boletimMedicao?.status === statusBoletimMedicaoEnum.AprovadoParaColetaAssinaturaFiscalTerceiro);
        },
        tipoAnexoAssinadoAtual() {
            if (this.$store.getters.isFornecedor && this.boletimMedicao?.status === statusBoletimMedicaoEnum.DevolvidoParaReassinarFornecedor) {
                return tiposAnexoEnum.CronogramaFisicoFinanceiroAssinadoPeloFornecedor;
            }
            const tipoPorStatus = {
                [statusBoletimMedicaoEnum.AprovadoParaColetaAssinaturaFornecedor]: tiposAnexoEnum.CronogramaFisicoFinanceiroAssinadoPeloFornecedor,
                [statusBoletimMedicaoEnum.AprovadoParaColetaAssinaturaFiscalTerceiro]: tiposAnexoEnum.CronogramaFisicoFinanceiroAssinadoPeloFiscalTerceiro,
            };

            return tipoPorStatus[this.boletimMedicao?.status] || null;
        },
        podeGerarPdfParaAssinatura() {
            return this.podeGerenciarAssinaturaAtual;
        },
        textoBotaoGerarPdfAssinatura() {
            return 'Gerar PDF para Assinatura';
        },
        nomeAnexoCronogramaAssinado() {
            const tipo = this.tipoAnexoAssinadoAtual;
            if (!tipo) return null;
            const anexo = (this.boletimMedicao?.anexos || []).find(a => a.tipoAnexoBoletimMedicao === tipo);
            return anexo ? (anexo.nome || 'Arquivo já enviado') : null;
        },
        documentoCronogramaAssinado() {
            const tipo = this.tipoAnexoAssinadoAtual;
            if (!tipo) return null;
            return (this.boletimMedicao?.anexos || []).find(a => a.tipoAnexoBoletimMedicao === tipo) || null;
        },
        srcVisualizacaoPdfCronograma() {
            if (!this.pdfUrl) return null;
            const [urlSemFragmento] = this.pdfUrl.split('#');
            return `${urlSemFragmento}#toolbar=0`;
        },
        nomeArquivoPreviewCronograma() {
            return this.montarNomeArquivoCronograma();
        },
    },
    watch: {
        contratoId() {
            this.carregarCronograma();
        },
        boletimMedicaoId() {
            this.carregarCronograma();
        },
        'boletimMedicao.itens': {
            handler() {
                if (this.itensInternos.length > 0) this.reprocessarEstruturaInterna();
            },
            deep: true
        },
        todosBoletinsMedicoes: {
            handler(newVal) {
                if (newVal && newVal.length > 0) this.reprocessarEstruturaInterna();
            },
            deep: true
        }
    },
    methods: {
        carregarCronograma() {
            if (this.temDadosExternos) return

            this.loading = true
            this.$store.dispatch('addRequest')

                        if (this.boletimMedicaoId) {

                            BoletimMedicaoServicos.obterPlanejamentoFisicoFinanceiro(this.contratoId, this.boletimMedicaoId)

                                .then(response => {

                                    if (response && response.success) {

                                        const dados = response.data

                                        this.dadosContrato = dados.contrato || {

                                            valorTotal: dados.valorTotalContrato,

                                            gestor: dados.gestor,

                                            dataVigencia: dados.vigencia

                                        }

                                        this.dadosBoletim = dados
                                        this.listaBoletinsDoAditivo = dados.listaBoletins || []

                                        this.itensInternos = this.garantirEstruturaItens(dados.itens, dados.numeroBoletim)
                                        this.totalInterno = this.garantirEstruturaTotal(dados.total, this.itensInternos, dados.numeroBoletim)

                                    } else if (response && response.notFound) {

                                        this.$toast.warning('Planejamento Físico-Financeiro não encontrado para este boletim.', 'Atenção');

                                    } else {

                                        this.$toast.error('Erro ao carregar cronograma', 'ERROR')

                                    }

                                })

                                .finally(() => {

                                    this.loading = false

                                    this.$store.dispatch('removeRequest')

                                })

                        }

             else {
                const id = this.contratoId || this.$route.params.codContrato || 0
                BoletimMedicaoServicos.obterPlanejamentoFisicoFinanceiroPorContrato(id)
                    .then(response => {
                        if (response && response.success) {
                            const dados = response.data
                            this.itensInternos = this.garantirEstruturaItens(dados.itens)
                            this.dadosContrato = dados.contrato || {
                                valorTotal: dados.valorTotalContrato,
                                gestor: dados.gestor,
                                dataVigencia: dados.vigencia
                            }
                            this.totalInterno = this.garantirEstruturaTotal(dados.total, this.itensInternos)
                            this.dadosBoletim = dados.boletim || {}
                        } else {
                            this.$toast.error('Erro ao carregar cronograma', 'ERROR')
                        }
                    })
                    .finally(() => {
                        this.loading = false
                        this.$store.dispatch('removeRequest')
                    })
            }
        },

        async gerarPdfParaAssinatura() {
            if (!this.podeGerarPdfParaAssinatura) {
                return;
            }

            if (this.nomeAnexoCronogramaAssinado) {
                this.viewMode = 'upload';
                return;
            }

            if (this.carregandoPdfParaAssinatura) {
                return;
            }

            this.carregandoPdfParaAssinatura = true;
            this.viewMode = 'pdf-viewer';
            window.scrollTo(0, 0);

            if (this.pdfUrl) {
                window.URL.revokeObjectURL(this.pdfUrl);
                this.pdfUrl = null;
            }

            const idContrato = this.contratoId || this.$route.params.codContrato;
            const idBoletim = this.boletimMedicaoId;

            let logoBase64 = null;
            if (this.logoFornecedorUrl) {
                logoBase64 = await this.urlToBase64(this.logoFornecedorUrl);
            }

            BoletimMedicaoServicos.obterPdfPlanejamentoFisicoFinanceiro(idContrato, idBoletim, logoBase64)
                .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    this.pdfUrl = url;
                })
                .catch(error => {
                    this.$toast.error('Erro ao gerar PDF para assinatura.', 'Erro');
                    this.viewMode = 'tabela';
                })
                .finally(() => {
                    this.carregandoPdfParaAssinatura = false;
                });
        },
        onVoltarUpload() {
            this.viewMode = 'pdf-viewer';
        },

        formatarPercentual(valor) {
            if (valor === undefined || valor === null) return 0;
            const num = parseFloat(valor);
            return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
        },
        reprocessarEstruturaInterna() {
            if (this.dadosBoletim && this.dadosBoletim.itens) {
                this.itensInternos = this.garantirEstruturaItens(this.dadosBoletim.itens, this.dadosBoletim.numeroBoletim);
                this.totalInterno = this.garantirEstruturaTotal(this.dadosBoletim.total, this.itensInternos, this.dadosBoletim.numeroBoletim);
            }
        },
        obterNumeroBoletim(valor) {
            if (valor === null || valor === undefined) return null;

            let numero = parseInt(valor, 10);
            if (!isNaN(numero) && numero > 0) return numero;

            const numeroExtraido = String(valor).match(/(\d+)/);
            if (!numeroExtraido || !numeroExtraido[1]) return null;

            numero = parseInt(numeroExtraido[1], 10);
            return isNaN(numero) || numero <= 0 ? null : numero;
        },
        obterIndiceBoletimAtual(numeroBoletim) {
            const numeroAtual = this.obterNumeroBoletim(numeroBoletim) || this.obterNumeroBoletim(this.indiceFixoBm);
            return numeroAtual ? numeroAtual - 1 : 0;
        },
        obterTotalBoletimPorNumero(numeroBoletim) {
            if (!Array.isArray(this.todosBoletinsMedicoes) || this.todosBoletinsMedicoes.length === 0) return null;

            const boletim = this.todosBoletinsMedicoes.find(b => this.obterNumeroBoletim(b.numero) === numeroBoletim);
            if (!boletim) return null;

            const total = boletim.total;
            return typeof total === 'number' ? total : parseFloat(total || 0);
        },
        obterValorBm(bm) {
            if (!bm) return 0;
            return bm.valor !== undefined && bm.valor !== null ? bm.valor : (bm.parcial || 0);
        },
        somarBmsAteIndice(bms, indiceLimite) {
            if (!Array.isArray(bms) || indiceLimite < 0) return 0;

            let total = 0;
            for (let i = 0; i <= indiceLimite; i++) {
                const bm = bms[i];
                if (!bm) continue;

                const valor = this.obterValorBm(bm);
                total += valor || 0;
            }

            return total;
        },
        obterItemBoletimAtual(itemContratoId) {
            if (!this.boletimMedicao || !Array.isArray(this.boletimMedicao.itens)) return null;
            if (itemContratoId === null || itemContratoId === undefined || String(itemContratoId).trim() === '') return null;

            return this.boletimMedicao.itens.find(i => String(i.itemContratoId) === String(itemContratoId)) || null;
        },
        normalizarCodigoItem(codigo) {
            if (codigo === null || codigo === undefined) return '';

            const texto = String(codigo).trim();
            if (!texto) return '';

            const match = texto.match(/^\d+(?:\.\d+)*\.?/);
            if (!match) return '';

            return match[0].replace(/\.$/, '');
        },
        extrairCodigoDescricao(descricao) {
            return this.normalizarCodigoItem(descricao);
        },
        normalizarDescricaoComparacao(descricao) {
            if (descricao === null || descricao === undefined) return '';

            return String(descricao)
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .trim();
        },
        removerCodigoDescricao(descricao) {
            if (descricao === null || descricao === undefined) return '';

            return String(descricao)
                .replace(/^\d+(?:\.\d+)*(?:\s*-\s*)?/, '')
                .trim();
        },
        obterCodigoPaiPorDescricao(descricaoCronograma) {
            if (!this.boletimMedicao || !Array.isArray(this.boletimMedicao.itens)) return '';

            const descricaoAlvo = this.normalizarDescricaoComparacao(this.removerCodigoDescricao(descricaoCronograma));
            if (!descricaoAlvo) return '';

            const itensPaiPlanilha = this.boletimMedicao.itens.filter(itemPlanilha => {
                const codigo = this.extrairCodigoDescricao(itemPlanilha.descricao);
                return codigo && !codigo.includes('.');
            });

            const itemPaiExato = itensPaiPlanilha.find(itemPlanilha => {
                const descricaoPai = this.normalizarDescricaoComparacao(this.removerCodigoDescricao(itemPlanilha.descricao));
                return descricaoPai === descricaoAlvo;
            });

            if (itemPaiExato) return this.extrairCodigoDescricao(itemPaiExato.descricao);

            const itemPaiAproximado = itensPaiPlanilha.find(itemPlanilha => {
                const descricaoPai = this.normalizarDescricaoComparacao(this.removerCodigoDescricao(itemPlanilha.descricao));
                return descricaoPai && (descricaoPai.includes(descricaoAlvo) || descricaoAlvo.includes(descricaoPai));
            });

            return itemPaiAproximado ? this.extrairCodigoDescricao(itemPaiAproximado.descricao) : '';
        },
        obterCodigoDoItemBoletimAtual(itemContratoId) {
            const itemBoletim = this.obterItemBoletimAtual(itemContratoId);
            if (!itemBoletim) return '';

            const codigoDireto = this.normalizarCodigoItem(
                itemBoletim.item || itemBoletim.ordem || itemBoletim.seqRM
            );
            if (codigoDireto) return codigoDireto;

            return this.extrairCodigoDescricao(itemBoletim.descricao);
        },
        obterCodigoHierarquiaItem(item = {}) {
            const codigoDireto = this.normalizarCodigoItem(item.item || item.ordem || item.seqRM);
            if (codigoDireto) return codigoDireto;

            const codigoDescricao = this.extrairCodigoDescricao(item.descricao);
            if (codigoDescricao) return codigoDescricao;

            const codigoPorDescricao = this.obterCodigoPaiPorDescricao(item.descricao);
            if (codigoPorDescricao) return codigoPorDescricao;

            return this.obterCodigoDoItemBoletimAtual(item.itemContratoId);
        },
        obterValorRealizadoDoItemBoletimAtual(itemContratoId) {
            const itemBm = this.obterItemBoletimAtual(itemContratoId);
            if (!itemBm) return null;

            return itemBm.valorTotal || itemBm.valorFinanceiro || itemBm.valorExecutado || 0;
        },
        obterPercentualRealizadoDoItemBoletimAtual(itemContratoId) {
            const itemBm = this.obterItemBoletimAtual(itemContratoId);
            if (!itemBm) return null;

            if (itemBm.percentualExecutado !== undefined && itemBm.percentualExecutado !== null) {
                return this.formatarPercentual(itemBm.percentualExecutado);
            }

            return null;
        },
        preencherRealizadoBmAtualSeAusente(bmsRealizado, indiceBoletimAtual, itemContratoId) {
            if (!Array.isArray(bmsRealizado) || indiceBoletimAtual < 0) return;
            if (bmsRealizado[indiceBoletimAtual]) return;

            const valorAtual = this.obterValorRealizadoDoItemBoletimAtual(itemContratoId);
            const percentualAtual = this.obterPercentualRealizadoDoItemBoletimAtual(itemContratoId);

            if (valorAtual !== null || percentualAtual !== null) {
                bmsRealizado[indiceBoletimAtual] = {
                    valor: valorAtual || 0,
                    percentual: this.formatarPercentual(percentualAtual)
                };
            }
        },
        aplicarFallbackTotaisBoletins(bmsRealizado, indiceBoletimAtual, item) {
            if (!Array.isArray(bmsRealizado)) return;

            for (let i = 0; i <= indiceBoletimAtual; i++) {
                const numeroBm = i + 1;
                const totalBoletim = this.obterTotalBoletimPorNumero(numeroBm);
                if (totalBoletim === null || totalBoletim === undefined) continue;

                const basePercentualItem = item.valorTotalItem || item.saldoValor || 0;
                const percentualBm = basePercentualItem ? (totalBoletim / basePercentualItem) * 100 : 0;

                if (!bmsRealizado[i] || (bmsRealizado[i].valor || 0) === 0) {
                    bmsRealizado[i] = {
                        valor: totalBoletim,
                        percentual: this.formatarPercentual(percentualBm)
                    };
                }
            }
        },

        garantirEstruturaItens(itens, numeroBoletim) {
            if (!itens || !Array.isArray(itens)) return []
            const indiceBoletimAtual = this.obterIndiceBoletimAtual(numeroBoletim);
            const usarFallbackPorTotalBoletins =
                itens.length === 1 &&
                Array.isArray(this.todosBoletinsMedicoes) &&
                this.todosBoletinsMedicoes.length > 0;

            return itens.map(item => {
                const codigoHierarquia = this.obterCodigoHierarquiaItem(item);
                const bmsPlanejado = [];
                const bmsRealizado = [];

                if (item.historicoMedicoes && Array.isArray(item.historicoMedicoes)) {
                    item.historicoMedicoes.forEach(hist => {
                        const numeroHist = this.obterNumeroBoletim(hist.numeroBoletim);
                        if (!numeroHist) return;

                        const idx = numeroHist - 1;
                        if (idx >= 0) {
                            bmsPlanejado[idx] = {
                                valor: hist.valorPlanejado || hist.valorCalculado || 0,
                                percentual: this.formatarPercentual(hist.percentualPlanejado)
                            };

                            if (idx <= indiceBoletimAtual) {
                                const valorRealizadoHistorico =
                                    hist.valorExecutado ??
                                    hist.valorRealizado ??
                                    (item.realizado && item.realizado.bms && item.realizado.bms[idx] &&
                                        this.obterValorBm(item.realizado.bms[idx]));
                                const percentualRealizadoHistorico =
                                    hist.percentualExecutado ??
                                    (item.realizado && item.realizado.bms && item.realizado.bms[idx] &&
                                        item.realizado.bms[idx].percentual);

                                bmsRealizado[idx] = {
                                    valor: valorRealizadoHistorico || 0,
                                    percentual: this.formatarPercentual(percentualRealizadoHistorico)
                                };
                            }
                        }
                    });
                } else if (item.medicoes && Array.isArray(item.medicoes)) {
                    item.medicoes.forEach(medicao => {
                        const numeroHist = this.obterNumeroBoletim(medicao.boletim);
                        if (!numeroHist) return;

                        const idx = numeroHist - 1;
                        if (idx < 0) return;

                        bmsPlanejado[idx] = {
                            valor: medicao.valorCalculado || medicao.valorPlanejado || 0,
                            percentual: this.formatarPercentual(medicao.percentualInformado || medicao.percentualPlanejado)
                        };

                        if (idx <= indiceBoletimAtual) {
                            const valorRealizadoMedicao =
                                medicao.valorExecutado ??
                                medicao.valorRealizado ??
                                (item.realizado && item.realizado.bms && item.realizado.bms[idx] &&
                                    this.obterValorBm(item.realizado.bms[idx]));
                            const percentualRealizadoMedicao =
                                medicao.percentualExecutado ??
                                (item.realizado && item.realizado.bms && item.realizado.bms[idx] &&
                                    item.realizado.bms[idx].percentual);

                            if (valorRealizadoMedicao !== undefined || percentualRealizadoMedicao !== undefined) {
                                bmsRealizado[idx] = {
                                    valor: valorRealizadoMedicao || 0,
                                    percentual: this.formatarPercentual(percentualRealizadoMedicao)
                                };
                            }
                        }
                    });
                } else {
                    const targetIdx = indiceBoletimAtual;
                    let valorExecutadoAtual = item.valorExecutado || this.obterValorRealizadoDoItemBoletimAtual(item.itemContratoId) || 0;
                    let percentualExecutadoAtual = item.percentualExecutado;

                    const percentualItemBmAtual = this.obterPercentualRealizadoDoItemBoletimAtual(item.itemContratoId);
                    if (percentualItemBmAtual !== null) {
                        percentualExecutadoAtual = percentualItemBmAtual;
                    }

                    if (item.valorPlanejado !== undefined || item.valorExecutado !== undefined || valorExecutadoAtual !== undefined) {
                        bmsPlanejado[targetIdx] = {
                            valor: item.valorPlanejado || 0,
                            percentual: this.formatarPercentual(item.percentualPlanejado)
                        };

                        bmsRealizado[targetIdx] = {
                            valor: valorExecutadoAtual,
                            percentual: this.formatarPercentual(percentualExecutadoAtual)
                        };
                    }
                }

                if (item.planejado && item.planejado.acumulado && !item.historicoMedicoes) {
                    const bmsRealizadoFiltrados = Array.isArray(item.realizado && item.realizado.bms)
                        ? item.realizado.bms.map((bm, idx) => {
                            if (idx > indiceBoletimAtual) return undefined;
                            if (!bm) return bm;

                            return {
                                ...bm,
                                valor: this.obterValorBm(bm),
                                percentual: this.formatarPercentual(bm.percentual)
                            };
                        })
                        : [];

                    this.preencherRealizadoBmAtualSeAusente(bmsRealizadoFiltrados, indiceBoletimAtual, item.itemContratoId);

                    if (usarFallbackPorTotalBoletins) {
                        this.aplicarFallbackTotaisBoletins(bmsRealizadoFiltrados, indiceBoletimAtual, item);
                    }

                    const valorRealizadoFiltrado = this.somarBmsAteIndice(bmsRealizadoFiltrados, indiceBoletimAtual);
                    const basePercentualItem = item.valorTotalItem || item.saldoValor || 0;
                    const percentualRealizadoFiltrado = basePercentualItem
                        ? (valorRealizadoFiltrado / basePercentualItem) * 100
                        : 0;

                    return {
                        ...item,
                        codigoHierarquia,
                        item: codigoHierarquia || item.item || item.ordem || item.seqRM || '',
                        valorTotalItem: item.valorTotalItem || item.saldoValor,
                        realizado: {
                            ...(item.realizado || {}),
                            acumulado: {
                                ...((item.realizado && item.realizado.acumulado) || {}),
                                valor: valorRealizadoFiltrado,
                                percentual: this.formatarPercentual(percentualRealizadoFiltrado)
                            },
                            bms: bmsRealizadoFiltrados
                        }
                    };
                }

                this.preencherRealizadoBmAtualSeAusente(bmsRealizado, indiceBoletimAtual, item.itemContratoId);

                if (usarFallbackPorTotalBoletins) {
                    this.aplicarFallbackTotaisBoletins(bmsRealizado, indiceBoletimAtual, item);
                }

                const valorAcumuladoExecutadoFiltrado = this.somarBmsAteIndice(bmsRealizado, indiceBoletimAtual);
                const basePercentualItem = item.valorTotalItem || item.saldoValor || 0;
                const percentualAcumuladoExecutadoFiltrado = basePercentualItem
                    ? (valorAcumuladoExecutadoFiltrado / basePercentualItem) * 100
                    : 0;

                return {
                    id: item.itemPlanejamentoId || item.itemContratoId,
                    itemContratoId: item.itemContratoId,
                    codigoHierarquia,
                    item: codigoHierarquia || item.item || item.ordem || item.seqRM || '',
                    descricao: item.descricao,
                    contrato: item.contrato || '',
                    valorTotalItem: item.valorTotalItem || item.saldoValor,
                    planejado: {
                        acumulado: {
                            valor: item.valorAcumuladoPlanejamento || 0,
                            percentual: this.formatarPercentual(item.percentualAcumuladoPlanejamento)
                        },
                        bms: bmsPlanejado
                    },
                    realizado: {
                        acumulado: {
                            valor: valorAcumuladoExecutadoFiltrado,
                            percentual: this.formatarPercentual(percentualAcumuladoExecutadoFiltrado)
                        },
                        bms: bmsRealizado
                    }
                }
            })
        },

        garantirEstruturaTotal(total, itensMapeados, numeroBoletim) {
            const indiceBoletimAtual = this.obterIndiceBoletimAtual(numeroBoletim);
            const numeroBoletimAtual = this.obterNumeroBoletim(numeroBoletim) || this.obterNumeroBoletim(this.indiceFixoBm) || 0;

            const valorTotalContrato = this.dadosContrato.valorTotal || this.dadosContrato.valorTotalContrato || 0

            const totalValorAcumuladoPlan = itensMapeados.reduce((acc, it) => acc + (it.planejado.acumulado.valor || 0), 0)
            const totalPctAcumuladoPlan = valorTotalContrato ? (totalValorAcumuladoPlan / valorTotalContrato) * 100 : 0

            const totalValorAcumuladoReal = itensMapeados.reduce((acc, it) => {
                return acc + this.somarBmsAteIndice(it.realizado && it.realizado.bms, indiceBoletimAtual);
            }, 0)
            const totalPctAcumuladoReal = valorTotalContrato ? (totalValorAcumuladoReal / valorTotalContrato) * 100 : 0

            const bmsPlanTotal = [];
            const bmsRealTotal = [];

            let maxBms = 0;
            if (numeroBoletimAtual > maxBms) maxBms = numeroBoletimAtual;

            itensMapeados.forEach(it => {
                if (it.planejado && it.planejado.bms && it.planejado.bms.length > maxBms) maxBms = it.planejado.bms.length;
            });

            for (let i = 0; i < maxBms; i++) {
                 const valParcialPlan = itensMapeados.reduce((acc, it) => acc + (it.planejado.bms[i] ? it.planejado.bms[i].valor : 0), 0);
                 const pctParcialPlan = valorTotalContrato ? (valParcialPlan / valorTotalContrato) * 100 : 0;

                 const previousAcumPlan = (i > 0 && bmsPlanTotal[i-1]) ? bmsPlanTotal[i-1].acumulado : 0;
                 const currentAcumPlan = previousAcumPlan + valParcialPlan;

                 bmsPlanTotal[i] = {
                    parcial: valParcialPlan,
                    acumulado: currentAcumPlan,
                    percentual: this.formatarPercentual(pctParcialPlan)
                 };

                 const valParcialReal = itensMapeados.reduce((acc, it) => {
                     const bmReal = it.realizado && it.realizado.bms && it.realizado.bms[i];
                     if (!bmReal) return acc;

                     const valorBm = this.obterValorBm(bmReal);
                     return acc + (valorBm || 0);
                 }, 0);
                 const pctParcialReal = valorTotalContrato ? (valParcialReal / valorTotalContrato) * 100 : 0;

                 const previousAcumReal = (i > 0 && bmsRealTotal[i-1]) ? bmsRealTotal[i-1].acumulado : 0;
                 const currentAcumReal = previousAcumReal + valParcialReal;

                 if (i <= indiceBoletimAtual) {
                    bmsRealTotal[i] = {
                       parcial: valParcialReal,
                       acumulado: currentAcumReal,
                       percentual: this.formatarPercentual(pctParcialReal)
                    };
                 }
            }

            return {
                planejado: {
                    acumulado: {
                        parcial: totalValorAcumuladoPlan,
                        acumulado: totalValorAcumuladoPlan,
                        percentual: this.formatarPercentual(totalPctAcumuladoPlan)
                    },
                    bms: bmsPlanTotal
                },
                realizado: {
                    acumulado: {
                        parcial: totalValorAcumuladoReal,
                        acumulado: totalValorAcumuladoReal,
                        percentual: this.formatarPercentual(totalPctAcumuladoReal)
                    },
                    bms: bmsRealTotal
                }
            }
        },

        mudarEtapa(novaEtapa) {
            this.viewMode = novaEtapa;
            window.scrollTo(0, 0);
        },

        voltarParaListaBoletins() {
            this.$router.go(-1);
        },

        normalizarParteNomeArquivo(valor, fallback = '') {
            const texto = (valor || '').toString().trim();
            if (!texto) return fallback;

            const semAcento = texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const limpo = semAcento
                .replace(/[\\/:*?"<>|]+/g, '_')
                .replace(/\s+/g, '_')
                .replace(/_+/g, '_')
                .replace(/^[_\.]+|[_\.]+$/g, '');

            return limpo || fallback;
        },

        montarNomeArquivoCronograma() {
            const nomeBm = this.normalizarParteNomeArquivo(
                this.boletimMedicao?.numero || this.boletimMedicao?.nome || this.dadosBoletimRelatorio?.numero || this.dadosBoletimRelatorio?.nome,
                'BM'
            );

            const numeroContrato = this.normalizarParteNomeArquivo(
                this.dadosContratoRelatorio?.codContrato
                || this.dadosContratoRelatorio?.vcCodigoEstruturado
                || this.$route?.params?.codContrato
                || this.contratoId,
                'Contrato'
            );

            return `CronoFisicoFin_${nomeBm}_${numeroContrato}.pdf`;
        },

        gerarPdfBrowser() {
            if (!this.pdfUrl) {
                this.$toast.warning('Gere o PDF antes de baixar.', 'Atenção');
                return;
            }

            const link = document.createElement('a');
            link.href = this.pdfUrl;
            link.download = this.montarNomeArquivoCronograma();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        onUploadSuccess() {
            this.viewMode = 'tabela'
            this.carregarCronograma()
        },

        async enviarArquivoAssinado() {
            if (!this.arquivoSelecionado) {
                this.$toast.warning('Selecione um arquivo para enviar.');
                return;
            }

            this.loadingUpload = true;
            this.$store.dispatch('addRequest');

            try {
                const dto = {
                    tipo: this.tipoAnexoAssinadoAtual,
                    arquivo: this.arquivoSelecionado
                };

                const cId = this.contratoId || this.$route.params.codContrato
                const bmId = this.boletimMedicaoId || 0

                const response = this.documentoCronogramaAssinado?.documentoBoletimMedicaoId
                    ? await BoletimMedicaoServicos.atualizarDocumentoBoletimMedicao(cId, bmId, this.documentoCronogramaAssinado.documentoBoletimMedicaoId, dto)
                    : await BoletimMedicaoServicos.adicionarDocumentoBoletimMedicao(cId, bmId, dto)

                if (!response || !response.success) {
                    const mensagemErro = response?.errors?.join?.('; ') || 'Não foi possível enviar o arquivo.'
                    throw new Error(mensagemErro)
                }

                this.$toast.success('Arquivo enviado com sucesso!')
                this.viewMode = 'upload'
                this.arquivoSelecionado = null
                this.$emit('recarregarDadosBoletim')
                this.carregarCronograma()
            } catch (error) {
                this.$toast.error(error?.message || 'Não foi possível enviar o arquivo.', 'Erro')
            } finally {
                this.loadingUpload = false
                this.$store.dispatch('removeRequest')
            }
        },

        async urlToBase64(url) {
            if (!url) return null;
            try {
                const response = await fetch(url);
                const blob = await response.blob();
                return await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (error) {
                console.error("Erro ao converter URL para Base64:", error);
                return null;
            }
        }
    },
    created() {
        this.carregarCronograma()
    },
    mounted() {
        if (this.podeGerarPdfParaAssinatura && this.nomeAnexoCronogramaAssinado) {
            this.viewMode = 'upload';
        }
    },
}
</script>

<style scoped>
.cronograma-container {
    width: 100%;
    position: relative;
    min-height: 400px;
}

.pdf-viewer-container {
    position: relative;
    min-height: 300px;
    width: 100%;
}

.pdf-file-name {
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #2d3439;
    word-break: break-all;
}

.loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.spinner-container {
    text-align: center;
}
</style>
